// scorecardUtils.js

/**
 * Evaluates a condition against a record
 * @param {string} condition - JavaScript condition as string
 * @param {Object} record - The record to evaluate against
 * @returns {boolean} Result of condition evaluation
 */
export const evaluateCondition = (condition, record) => {
    try {
        return new Function('record', `return ${condition}`)(record);
    } catch (error) {
        console.error('Error evaluating condition:', error);
        return false;
    }
};

/**
 * Calculate color based on conditions
 * @param {number} value - The value to evaluate
 * @param {Array} conditions - Array of color conditions
 * @param {Object} defaultStyle - Default style to use if no conditions match
 * @returns {Object} Style object with color properties
 */
export const calculateColor = (value, conditions = [], defaultStyle = {}) => {
    if (!conditions || conditions.length === 0) {
        return defaultStyle;
    }

    // Sort conditions by threshold in descending order for '>=' operators
    // and ascending order for '<=' operators to ensure proper evaluation
    const greaterThanConditions = conditions
        .filter(c => c.operator === '>=')
        .sort((a, b) => a.threshold - b.threshold);

    const lessThanConditions = conditions
        .filter(c => c.operator === '<=')
        .sort((a, b) => b.threshold - a.threshold);

    // Check greater than conditions first
    for (const condition of greaterThanConditions) {
        if (value >= condition.threshold) {
            return {
                backgroundColor: condition.color,
                color: getContrastColor(condition.color)
            };
        }
    }

    // Then check less than conditions
    for (const condition of lessThanConditions) {
        if (value <= condition.threshold) {
            return {
                backgroundColor: condition.color,
                color: getContrastColor(condition.color)
            };
        }
    }

    return defaultStyle;
};

/**
 * Calculate metric value based on configuration
 * @param {Array} records - Array of records to calculate metric from
 * @param {Object} metricConfig - Configuration for the metric
 * @returns {number} Calculated metric value
 */
export const calculateMetricValue = (records, metricConfig) => {
    //console.log("Calculating Metric " + JSON.stringify(metricConfig));
    if (!records || records.length === 0 || !metricConfig) return 0;

    const { calculation = {} } = metricConfig;
    const { type, fields, field, condition } = calculation;

    //console.log(`Calculating ${metricConfig.id} with type ${type} on ${records.length} records`);

    // If there's a condition, filter
    const filtered = condition
        ? records.filter((r) => evaluateCondition(condition, r))
        : records;

    if (filtered.length === 0) return 0;

    try {
        switch (type) {
            case 'average': {
                // Handle single field vs multiple fields
                if (field) {
                    // Single field average - filter out non-numeric values
                    let sum = 0;
                    let count = 0;
console.log('Doing single field average');
                    for (const record of filtered) {
                        const val = record[field];
                        console.log('Value = ' + val);
                        // Only add numbers
                        if (val !== null && val !== undefined && !isNaN(Number(val))) {
                            sum += Number(val);
                            count++;
                        }
                    }
                    console.log('Count = ' + count);
                    console.log('Sum = ' + sum);
                    // Return average if we have values, 0 otherwise
                    return count > 0 ? sum / count : 0;

                } else if (fields && fields.length > 0) {
                    // Multiple fields average (e.g., quality score from prof and prot)
                    let sum = 0;
                    let count = 0;

                    for (const record of filtered) {
                        let recordSum = 0;
                        let validFields = 0;

                        for (const fieldName of fields) {
                            const val = record[fieldName];
                            // Only count values that are numbers
                            if (val !== null && val !== undefined && !isNaN(Number(val))) {
                                recordSum += Number(val);
                                validFields++;
                            }
                        }

                        // Add to total only if there are valid fields in this record
                        if (validFields > 0) {
                            sum += recordSum / validFields;
                            count++;
                        }
                    }

                    return count > 0 ? sum / count : 0;
                }
                return 0;
            }

            case 'count':
                return filtered.length;

            case 'percentage': {
                // (# passing condition / total) * 100
                return (filtered.length / records.length) * 100;
            }

            case 'value': {
                // Just return the raw field value (first record)
                if (!field || filtered.length === 0) return 0;

                const value = filtered[0][field];
                if (value === null || value === undefined) return 0;

                const numVal = Number(value);
                return !isNaN(numVal) ? numVal : 0;
            }

            case 'min': {
                if (!field) return 0;

                const validValues = [];
                for (const record of filtered) {
                    const val = record[field];
                    if (val !== null && val !== undefined && !isNaN(Number(val))) {
                        validValues.push(Number(val));
                    }
                }

                return validValues.length > 0 ? Math.min(...validValues) : 0;
            }

            case 'max': {
                if (!field) return 0;

                const validValues = [];
                for (const record of filtered) {
                    const val = record[field];
                    if (val !== null && val !== undefined && !isNaN(Number(val))) {
                        validValues.push(Number(val));
                    }
                }

                return validValues.length > 0 ? Math.max(...validValues) : 0;
            }

            default:
                console.warn(`Unknown calculation type: ${type}`);
                return 0;
        }
    } catch (error) {
        console.error("Error in calculateMetricValue:", error, { metricConfig, records });
        return 0; // Return 0 instead of NaN on error
    }
};

// Updates to findMinValue and findMaxValue (less aggressive)
export function findMinValue(records, metricDef) {
    if (!records || !records.length) return 0;

    try {
        const values = [];

        // Calculate value for each record
        for (const record of records) {
            const val = calculateMetricValue([record], metricDef);
            // Only include actual numeric values
            if (val !== null && val !== undefined && !isNaN(val)) {
                values.push(val);
            }
        }

        // Return min or 0 if no valid values
        return values.length > 0 ? Math.min(...values) : 0;
    } catch (error) {
        console.error("Error in findMinValue:", error);
        return 0;
    }
}

export function findMaxValue(records, metricDef) {
    if (!records || !records.length) return 0;

    try {
        const values = [];

        // Calculate value for each record
        for (const record of records) {
            const val = calculateMetricValue([record], metricDef);
            // Only include actual numeric values
            if (val !== null && val !== undefined && !isNaN(val)) {
                values.push(val);
            }
        }

        // Return max or 0 if no valid values
        return values.length > 0 ? Math.max(...values) : 0;
    } catch (error) {
        console.error("Error in findMaxValue:", error);
        return 0;
    }
}


/**
 * Calculate difference from average
 * @param {number} value - Current value
 * @param {number} average - Average value to compare against
 * @param {string} format - Format of the difference ('percentage' or 'absolute')
 * @returns {number} Formatted difference value
 */
export const calculateDifference = (value, average, format = 'percentage') => {
    if (format === 'percentage') {
        return average !== 0 ? ((value - average) / average) * 100 : 0;
    }
    return value - average;
};

/**
 * Get contrasting text color for a background color
 * @param {string} backgroundColor - Background color in hex format
 * @returns {string} Either black or white depending on background
 */
export const getContrastColor = (backgroundColor) => {
    // Convert hex to RGB
    const hex = backgroundColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    return luminance > 0.5 ? '#000000' : '#ffffff';
};

/**
 * Format metric value based on type
 * @param {number} value - Value to format
 * @param {Object} metricConfig - Metric configuration
 * @returns {string} Formatted value
 */
export const formatMetricValue = (value, metricConfig) => {
    const { calculation } = metricConfig;

    switch (calculation.type) {
        case 'percentage':
            return `${value.toFixed(1)}%`;
        case 'count':
            return Math.round(value).toString();
        default:
            return value.toFixed(1);
    }
};

/**
 * Calculate overall averages for comparison
 * @param {Array} records - All records
 * @param {Array} metrics - Metric configurations
 * @returns {Object} Object containing averages for each metric
 */
export const calculateOverallAverages = (records, metrics) => {
    const averages = {};

    metrics.forEach(metric => {
        averages[metric.id] = calculateMetricValue(records, metric);
    });

    return averages;
};
